// A focus-trapped modal dialog.
//
// Written by hand rather than pulled from a library because the focus contract is the
// component: focus moves in on open, cannot leave while open, returns exactly where it
// came from on close, and Escape always works. Those four behaviours are what make a
// modal usable without a mouse, and each one is tested in Dialog.test.tsx.

import { useCallback, useEffect, useId, useRef, type ReactNode } from "react";

import { cx } from "./styles";

/**
 * Elements that can hold focus. `[tabindex="-1"]` is deliberately excluded: it is
 * programmatically focusable but deliberately outside the tab order, so trapping Tab
 * onto it would be wrong.
 */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface DialogProps {
  readonly open: boolean;
  /** Accessible name for the dialog. Caller-supplied text. */
  readonly title: string;
  readonly children: ReactNode;
  readonly onClose: () => void;
  /** Footer actions, e.g. confirm/cancel buttons. */
  readonly footer?: ReactNode;
}

export function Dialog({ open, title, children, onClose, footer }: DialogProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const titleId = useId();

  const focusable = useCallback((): HTMLElement[] => {
    const root = dialogRef.current;
    if (root === null) return [];

    // Filter on SEMANTIC hiding, not on layout.
    //
    // The obvious filter is `offsetParent !== null`, and it is wrong twice over: it
    // depends on layout, which jsdom does not perform, so the trap silently degrades
    // to "no focusable elements" under test; and in real browsers `offsetParent` is
    // null for position:fixed subtrees — which is exactly what a modal is. Checking
    // the attributes that actually mean "hidden" works identically everywhere.
    return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
      (element) =>
        !element.hasAttribute("hidden") &&
        element.getAttribute("aria-hidden") !== "true" &&
        element.closest("[inert]") === null,
    );
  }, []);

  // Remember where focus came from, and move it into the dialog.
  useEffect(() => {
    if (!open) return;

    returnFocusRef.current = document.activeElement as HTMLElement | null;

    const first = focusable()[0];
    // Falling back to the dialog itself means focus is never left on the page behind
    // an open modal, even for a dialog with no focusable content.
    (first ?? dialogRef.current)?.focus();

    return () => {
      // Returning focus to the trigger is what keeps keyboard navigation coherent:
      // without it the user is dumped back at the top of the document.
      returnFocusRef.current?.focus();
    };
  }, [open, focusable]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== "Tab") return;

      const elements = focusable();
      if (elements.length === 0) {
        event.preventDefault();
        return;
      }

      const first = elements[0];
      const last = elements[elements.length - 1];
      const active = document.activeElement;

      // Wrap at both ends. Without this, Tab walks out of the dialog and into the
      // page behind it, which is invisible to a sighted keyboard user and chaotic
      // with a screen reader.
      if (event.shiftKey && (active === first || active === dialogRef.current)) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose, focusable]);

  if (!open) return null;

  return (
    <div
      className={cx.dialogBackdrop}
      // Clicking the backdrop closes; clicking inside must not. Checking the target
      // is the element itself avoids closing on every click that bubbles up.
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className={cx.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <h2 className={cx.dialogTitle} id={titleId}>
          {title}
        </h2>
        {children}
        {footer}
      </div>
    </div>
  );
}
